import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const RealtimeContext = createContext(null);

export const useRealtime = () => {
  const ctx = useContext(RealtimeContext);
  if (!ctx) return { connected: false, lastEvent: null, subscribe: () => () => {} };
  return ctx;
};

export const useRealtimeEvent = (eventType, callback) => {
  const { subscribe } = useRealtime();
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const handler = (data) => cbRef.current(data);
    const unsub = subscribe(eventType, handler);
    return unsub;
  }, [eventType, subscribe]);
};

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const subscribersRef = useRef(new Map());
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const pollRef = useRef(null);
  const lastPollTsRef = useRef(new Date().toISOString());
  const processedIdsRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const retriesRef = useRef(0);

  const subscribe = useCallback((eventType, callback) => {
    if (!subscribersRef.current.has(eventType)) {
      subscribersRef.current.set(eventType, new Set());
    }
    subscribersRef.current.get(eventType).add(callback);
    return () => {
      const subs = subscribersRef.current.get(eventType);
      if (subs) subs.delete(callback);
    };
  }, []);

  const dispatch = useCallback((eventType, data) => {
    // Dedup: build key from event data to prevent double-dispatch
    const id = data?.data?.id || '';
    const ts = data?.timestamp || '';
    const key = `${eventType}-${id}-${ts}`;
    if (processedIdsRef.current.has(key)) return;
    processedIdsRef.current.add(key);
    setTimeout(() => processedIdsRef.current.delete(key), 20000);

    setLastEvent({ type: eventType, data, timestamp: Date.now() });
    const subs = subscribersRef.current.get(eventType);
    if (subs) subs.forEach(cb => { try { cb(data); } catch (e) { console.error('Event error:', e); } });
    const wildcard = subscribersRef.current.get('*');
    if (wildcard) wildcard.forEach(cb => { try { cb({ type: eventType, ...data }); } catch {} });
  }, []);

  // ===== WebSocket (fast path - instant delivery) =====
  useEffect(() => {
    const token = localStorage.getItem('maintix_token');
    if (!user || !token) return;

    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/notifications?token=${token}`;

    const connect = () => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          retriesRef.current = 0;
          if (pingRef.current) clearInterval(pingRef.current);
          pingRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'ping' })); } catch {}
            }
          }, 20000);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (['heartbeat', 'pong', 'connected'].includes(msg.type)) return;
            dispatch(msg.type, msg);
          } catch {}
        };

        ws.onclose = () => {
          setConnected(false);
          if (pingRef.current) clearInterval(pingRef.current);
          const delay = Math.min(1000 * Math.pow(2, retriesRef.current), 15000);
          retriesRef.current++;
          reconnectRef.current = setTimeout(connect, delay);
        };

        ws.onerror = () => { setConnected(false); };
      } catch {
        const delay = Math.min(1000 * Math.pow(2, retriesRef.current), 15000);
        retriesRef.current++;
        reconnectRef.current = setTimeout(connect, delay);
      }
    };

    connect();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        retriesRef.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        retriesRef.current = 0;
        setTimeout(connect, 500);
      }
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wsRef.current) try { wsRef.current.close(); } catch {}
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
    };
  }, [user, dispatch]);

  // ===== HTTP Polling (reliable path - ALWAYS runs, dedup handles overlap with WS) =====
  useEffect(() => {
    if (!user) return;

    const poll = async () => {
      try {
        const since = encodeURIComponent(lastPollTsRef.current);
        const res = await api.get(`/events/poll?since=${since}`);
        const events = res.data || [];
        for (const evt of events) {
          dispatch(evt.type, evt);
          if (evt.timestamp && evt.timestamp > lastPollTsRef.current) {
            lastPollTsRef.current = evt.timestamp;
          }
        }
      } catch {}
    };

    // Poll immediately on mount, then every 3 seconds
    poll();
    pollRef.current = setInterval(poll, 3000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, dispatch]);

  const value = { connected, lastEvent, subscribe };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};
